import React, { useEffect, useState } from 'react';
import { WifiOff, RefreshCw, CheckCircle2 } from 'lucide-react';
import { db } from '../../db';

export interface StatusIndicatorState {
  label: string;
  color: string;
  icon: 'wifi-off' | 'refresh' | 'check-circle';
}

export function computeStatusIndicator(isOnline: boolean, pendingCount: number): StatusIndicatorState {
  if (!isOnline) return { label: 'Offline', color: '#EF4444', icon: 'wifi-off' };
  if (pendingCount > 0) return { label: `Pending Sync (${pendingCount})`, color: '#F59E0B', icon: 'refresh' };
  return { label: 'Synced', color: '#10B981', icon: 'check-circle' };
}

export interface OfflineStatusBadgeProps {
  className?: string;
  showText?: boolean;
  onManualSync?: () => void;
}

export const OfflineStatusBadge: React.FC<OfflineStatusBadgeProps> = ({
  className = '',
  showText = true,
  onManualSync,
}) => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);

  // Query Dexie queue
  const checkPendingQueue = async () => {
    try {
      const queueCount = await db.syncQueue
        .where('status')
        .anyOf('PENDING', 'FAILED')
        .count();
      const inspectionPendingCount = await db.inspections
        .where('syncStatus')
        .equals('PENDING_SYNC')
        .count();
      setPendingCount(Math.max(queueCount, inspectionPendingCount));
    } catch (e) {
      console.warn('[OfflineBadge] Failed to query pending sync count:', e);
    }
  };

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      checkPendingQueue();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check and periodic polling
    checkPendingQueue();
    const interval = setInterval(checkPendingQueue, 5000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  const handleClick = async () => {
    if (onManualSync) {
      setIsSyncing(true);
      try {
        await onManualSync();
        await checkPendingQueue();
      } finally {
        setIsSyncing(false);
      }
    } else {
      await checkPendingQueue();
    }
  };

  const status = computeStatusIndicator(isOnline, pendingCount);

  if (!isOnline) {
    return (
      <button
        type="button"
        onClick={handleClick}
        title="Offline Mode - Changes are saved locally"
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 shadow-xs min-h-[36px] active:scale-95 transition-all cursor-pointer ${className}`}
      >
        <WifiOff className="w-4 h-4 text-rose-600 shrink-0 animate-pulse" />
        {showText && <span>{status.label}</span>}
        {pendingCount > 0 && (
          <span className="bg-rose-600 text-white text-[10px] px-1.5 py-0.2 rounded-full font-bold">
            {pendingCount}
          </span>
        )}
      </button>
    );
  }

  if (pendingCount > 0 || isSyncing) {
    return (
      <button
        type="button"
        onClick={handleClick}
        title="Pending Sync - Click to attempt sync now"
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 shadow-xs min-h-[36px] active:scale-95 transition-all cursor-pointer ${className}`}
      >
        <RefreshCw className={`w-4 h-4 text-amber-600 shrink-0 ${isSyncing ? 'animate-spin' : ''}`} />
        {showText && <span>{status.label}</span>}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      title="All records synchronized"
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-xs min-h-[36px] active:scale-95 transition-all cursor-pointer ${className}`}
    >
      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
      {showText && <span>{status.label}</span>}
    </button>
  );
};
